import type { CarrierZoneEntries, ZipZoneMapping } from '../../../store/services/ZipZoneService/types';
import type { ZipZoneRecord, ZipZoneType } from './ZipZoneUpload';
import { ZipZoneTypes } from './ZipZoneUpload';

export const COLUMN_FROM_COUNTRY = 'From Country Code';
export const COLUMN_FROM_COUNTRY_KEY = 'fromCountryCode';
export const COLUMN_FROM_POSTAL = 'From Postal Code';
export const COLUMN_FROM_POSTAL_KEY = 'fromPostalCode';
export const COLUMN_TO_COUNTRY = 'To Country Code';
export const COLUMN_TO_COUNTRY_KEY = 'toCountryCode';
export const COLUMN_TO_POSTAL = 'To Postal Code';
export const COLUMN_TO_POSTAL_KEY = 'toPostalCode';
export const COLUMN_VALID = 'Is Valid';
export const COLUMN_VALID_KEY = 'zipZoneStatus';
export const COLUMN_ZONE = 'Zone';
export const COLUMN_ZONE_KEY = 'zone';
export const DEFAULT_COUNTRY = 'US';
export const DEFAULT_DESTINATION_ZIP5 = '48336';
export const DEFAULT_ORIGIN_ZIP3 = '599';
export const DEFAULT_ORIGIN_ZIP5 = '01599';
export const DEFAULT_ZONE = 'example-fedex-zone-1';
export const DESTINATION_COUNTRY = 'destinationCountry';
export const DESTINATION_POSTAL = 'destinationPostal';
export const ORIGIN_COUNTRY = 'originCountry';
export const ORIGIN_POSTAL = 'originPostal';
export const ZONES = 'zones';

export const generateZipZoneTemplateMatrix = (type: ZipZoneType): string[][] => {
  if (type === ZipZoneTypes.COUNTRY_TO_COUNTRY) {
    return [
      [ORIGIN_COUNTRY, DESTINATION_COUNTRY, ZONES],
      [DEFAULT_COUNTRY, DEFAULT_COUNTRY, DEFAULT_ZONE],
    ];
  } else if (type === ZipZoneTypes.COUNTRY_TO_POSTAL5) {
    return [
      [ORIGIN_COUNTRY, DESTINATION_POSTAL, DESTINATION_COUNTRY, ZONES],
      [DEFAULT_COUNTRY, DEFAULT_DESTINATION_ZIP5, DEFAULT_COUNTRY, DEFAULT_ZONE],
    ];
  } else if (type === ZipZoneTypes.TYPE3_TO_TYPE5) {
    return [
      [ORIGIN_POSTAL, ORIGIN_COUNTRY, DESTINATION_POSTAL, DESTINATION_COUNTRY, ZONES],
      [DEFAULT_ORIGIN_ZIP3, DEFAULT_COUNTRY, DEFAULT_DESTINATION_ZIP5, DEFAULT_COUNTRY, DEFAULT_ZONE],
    ];
  } else if (type === ZipZoneTypes.TYPE5_TO_TYPE5) {
    return [
      [ORIGIN_POSTAL, ORIGIN_COUNTRY, DESTINATION_POSTAL, DESTINATION_COUNTRY, ZONES],
      [DEFAULT_ORIGIN_ZIP5, DEFAULT_COUNTRY, DEFAULT_DESTINATION_ZIP5, DEFAULT_COUNTRY, DEFAULT_ZONE],
    ];
  } else {
    return [[]];
  }
};

export const extractZipZoneData = (arr: string[], index: number): string => {
  if (index < 0 || index >= arr.length) {
    return '';
  }
  return arr[index];
};

export const extractZipZoneDataFromMatrix = (csvMatrix: string[][]): ZipZoneRecord[] => {
  const records: ZipZoneRecord[] = [];

  if (csvMatrix.length <= 0) {
    return records;
  }

  let fromCountryCodeIndex = -1;
  let fromPostalCodeIndex = -1;
  let toCountryCodeIndex = -1;
  let toPostalCodeIndex = -1;
  let zoneIndex = -1;

  csvMatrix[0].forEach((value, index) => {
    switch (value) {
      case ORIGIN_COUNTRY:
        fromCountryCodeIndex = index;
        break;
      case ORIGIN_POSTAL:
        fromPostalCodeIndex = index;
        break;
      case DESTINATION_COUNTRY:
        toCountryCodeIndex = index;
        break;
      case DESTINATION_POSTAL:
        toPostalCodeIndex = index;
        break;
      case ZONES:
        zoneIndex = index;
        break;
    }
  });

  for (let i = 1; i < csvMatrix.length; i++) {
    records.push({
      fromPostalCode: extractZipZoneData(csvMatrix[i], fromPostalCodeIndex),
      fromCountryCode: extractZipZoneData(csvMatrix[i], fromCountryCodeIndex),
      toPostalCode: extractZipZoneData(csvMatrix[i], toPostalCodeIndex),
      toCountryCode: extractZipZoneData(csvMatrix[i], toCountryCodeIndex),
      zone: extractZipZoneData(csvMatrix[i], zoneIndex),
    });
  }

  return records;
};

export const generateZipZonePreviewTableColumns = (
  type: ZipZoneType,
  statusDefinition: (data: ZipZoneRecord) => any,
): any[] => {
  switch (type) {
    case ZipZoneTypes.COUNTRY_TO_COUNTRY:
      return [
        {
          title: COLUMN_FROM_COUNTRY,
          dataIndex: COLUMN_FROM_COUNTRY_KEY,
          key: COLUMN_FROM_COUNTRY_KEY,
        },
        {
          title: COLUMN_TO_COUNTRY,
          dataIndex: COLUMN_TO_COUNTRY_KEY,
          key: COLUMN_TO_COUNTRY_KEY,
        },
        {
          title: COLUMN_ZONE,
          dataIndex: COLUMN_ZONE_KEY,
          key: COLUMN_ZONE_KEY,
        },
        {
          title: COLUMN_VALID,
          key: COLUMN_VALID_KEY,
          render: (row: ZipZoneRecord) => {
            return statusDefinition(row);
          },
        },
      ];
    case ZipZoneTypes.COUNTRY_TO_POSTAL5:
      return [
        {
          title: COLUMN_FROM_COUNTRY,
          dataIndex: COLUMN_FROM_COUNTRY_KEY,
          key: COLUMN_FROM_COUNTRY_KEY,
        },
        {
          title: COLUMN_TO_COUNTRY,
          dataIndex: COLUMN_TO_COUNTRY_KEY,
          key: COLUMN_TO_COUNTRY_KEY,
        },
        {
          title: COLUMN_TO_POSTAL,
          dataIndex: COLUMN_TO_POSTAL_KEY,
          key: COLUMN_TO_POSTAL_KEY,
        },
        {
          title: COLUMN_ZONE,
          dataIndex: COLUMN_ZONE_KEY,
          key: COLUMN_ZONE_KEY,
        },
        {
          title: COLUMN_VALID,
          key: COLUMN_VALID_KEY,
          render: (row: ZipZoneRecord) => {
            return statusDefinition(row);
          },
        },
      ];
    case ZipZoneTypes.TYPE5_TO_TYPE5:
    case ZipZoneTypes.TYPE3_TO_TYPE5:
      return [
        {
          title: COLUMN_FROM_COUNTRY,
          dataIndex: COLUMN_FROM_COUNTRY_KEY,
          key: COLUMN_FROM_COUNTRY_KEY,
        },
        {
          title: COLUMN_FROM_POSTAL,
          dataIndex: COLUMN_FROM_POSTAL_KEY,
          key: COLUMN_FROM_POSTAL_KEY,
        },
        {
          title: COLUMN_TO_COUNTRY,
          dataIndex: COLUMN_TO_COUNTRY_KEY,
          key: COLUMN_TO_COUNTRY_KEY,
        },
        {
          title: COLUMN_TO_POSTAL,
          dataIndex: COLUMN_TO_POSTAL_KEY,
          key: COLUMN_TO_POSTAL_KEY,
        },
        {
          title: COLUMN_ZONE,
          dataIndex: COLUMN_ZONE_KEY,
          key: COLUMN_ZONE_KEY,
        },
        {
          title: COLUMN_VALID,
          key: COLUMN_VALID_KEY,
          render: (row: ZipZoneRecord) => {
            return statusDefinition(row);
          },
        },
      ];
    default:
      return [[]];
  }
};

export const stringArrayToList = (val: string[]) => {
  return (
    <ul style={{ margin: 0, padding: 0, listStyleType: 'none' }}>
      {val.map((s, i) => (
        <li key={i}>{s}</li>
      ))}
    </ul>
  );
};

export const generateZipZoneUploadAddedOrRemovedColumns = (): any[] => {
  return [
    {
      title: 'Key',
      dataIndex: 'key',
      key: 'key',
    },
    {
      title: 'Zones',
      dataIndex: 'zones',
      key: 'zones',
      render: stringArrayToList,
    },
  ];
};

export const generateZipZoneUploadModifiedColumns = (): any[] => {
  return [
    {
      title: 'Key',
      dataIndex: 'key',
      key: 'key',
    },
    {
      title: 'Zones - Before',
      dataIndex: 'before',
      key: 'before',
      render: stringArrayToList,
    },
    {
      title: 'Zones - After',
      dataIndex: 'after',
      key: 'after',
      render: stringArrayToList,
    },
  ];
};

export const convertToZipZoneMappings = (zoneKeyType: string, response: CarrierZoneEntries): ZipZoneMapping[] => {
  const mappings: ZipZoneMapping[] = [];
  if (zoneKeyType === 'any_country_to_any_country') {
    response.anyCountryToAnyCountry.forEach((countryZoneEntry) => {
      mappings.push({
        carrier: response.carrierId,
        fromCountryCode: response.originCountry,
        destinationCountryCode: countryZoneEntry.country,
        zones: countryZoneEntry.zones,
      });
    });
  } else if (zoneKeyType === 'any_country_to_postal') {
    response.anyCountryToPostal.forEach((countryZoneEntry) => {
      countryZoneEntry.postalCodes.forEach((postalZoneEntry) => {
        mappings.push({
          carrier: response.carrierId,
          fromCountryCode: response.originCountry,
          destinationCountryCode: countryZoneEntry.country,
          destinationPostalCode: postalZoneEntry.postal,
          zones: postalZoneEntry.zones,
        });
      });
    });
  } else if (zoneKeyType === 'any_US_origin_to_postal_group') {
    response.anyUSOriginToPostalGroup.forEach((countryZoneEntry) => {
      countryZoneEntry.postalCodes.forEach((postalZoneEntry) => {
        mappings.push({
          carrier: response.carrierId,
          fromCountryCode: response.originCountry,
          destinationCountryCode: countryZoneEntry.country,
          destinationPostalCode: postalZoneEntry.postal,
          zones: postalZoneEntry.zones,
        });
      });
    });
  } else if (zoneKeyType === 'postal_to_postal') {
    response.postalToPostal.forEach((postalToCountryToPostalZoneEntry) => {
      postalToCountryToPostalZoneEntry.countries.forEach((countryToPostalZoneEntry) => {
        countryToPostalZoneEntry.postalCodes.forEach((postalZoneEntry) => {
          mappings.push({
            carrier: response.carrierId,
            fromCountryCode: response.originCountry,
            destinationCountryCode: countryToPostalZoneEntry.country,
            destinationPostalCode: postalZoneEntry.postal,
            fromPostalCode: postalToCountryToPostalZoneEntry.postal,
            zones: postalZoneEntry.zones,
          });
        });
      });
    });
  } else if (zoneKeyType === 'postal3_to_any_country') {
    response.postal3ToAnyCountry.forEach((postalToCountryToPostalZoneEntry) => {
      postalToCountryToPostalZoneEntry.countries.forEach((countryToPostalZoneEntry) => {
        countryToPostalZoneEntry.postalCodes.forEach((postalZoneEntry) => {
          mappings.push({
            carrier: response.carrierId,
            fromCountryCode: response.originCountry,
            destinationCountryCode: countryToPostalZoneEntry.country,
            destinationPostalCode: postalZoneEntry.postal,
            fromPostalCode: postalToCountryToPostalZoneEntry.postal,
            zones: postalZoneEntry.zones,
          });
        });
      });
    });
  } else if (zoneKeyType === 'postal3_to_postal') {
    response.postal3ToPostal.forEach((postalToCountryToPostalZoneEntry) => {
      postalToCountryToPostalZoneEntry.countries.forEach((countryToPostalZoneEntry) => {
        countryToPostalZoneEntry.postalCodes.forEach((postalZoneEntry) => {
          mappings.push({
            carrier: response.carrierId,
            fromCountryCode: response.originCountry,
            destinationCountryCode: countryToPostalZoneEntry.country,
            destinationPostalCode: postalZoneEntry.postal,
            fromPostalCode: postalToCountryToPostalZoneEntry.postal,
            key_type: zoneKeyType,
            zones: postalZoneEntry.zones,
          });
        });
      });
    });
  } else if (zoneKeyType === 'postal3_to_postal1') {
    response.postal3ToPostal1.forEach((postalToCountryToPostalZoneEntry) => {
      postalToCountryToPostalZoneEntry.countries.forEach((countryToPostalZoneEntry) => {
        countryToPostalZoneEntry.postalCodes.forEach((postalZoneEntry) => {
          mappings.push({
            carrier: response.carrierId,
            fromCountryCode: response.originCountry,
            destinationCountryCode: countryToPostalZoneEntry.country,
            destinationPostalCode: postalZoneEntry.postal,
            fromPostalCode: postalToCountryToPostalZoneEntry.postal,
            key_type: zoneKeyType,
            zones: postalZoneEntry.zones,
          });
        });
      });
    });
  } else if (zoneKeyType === 'state_group_to_any_country') {
    response.stateGroupToAnyCountry.forEach((regionToCountryZoneEntry) => {
      regionToCountryZoneEntry.countries.forEach((countryZoneEntry) => {
        mappings.push({
          carrier: response.carrierId,
          fromCountryCode: response.originCountry,
          destinationCountryCode: countryZoneEntry.country,
          key_type: zoneKeyType,
          zones: countryZoneEntry.zones,
        });
      });
    });
  } else if (zoneKeyType === 'state_group_to_postal_group') {
    response.stateGroupToAnyCountry.forEach((countryToPostalZoneEntry) => {
      countryToPostalZoneEntry.countries.forEach((postalZoneEntry) => {
        mappings.push({
          carrier: response.carrierId,
          fromCountryCode: response.originCountry,
          destinationCountryCode: postalZoneEntry.country,
          key_type: zoneKeyType,
          zones: postalZoneEntry.zones,
        });
      });
    });
  }
  return mappings;
};
